/**
 * On-disk shape of `<gameDir>/.foundry/build-game-interview.json`.
 *
 * Source of truth for the `/build-game` skill's interview state. The
 * skill is the sole writer; the renderer reads via the
 * `build-game:get-interview-state` IPC channel and never mutates the
 * file directly. Edit intents go through the durable outbox instead
 * (`<gameDir>/.foundry/build-game-edit-outbox.json`).
 *
 * See `docs/plan-build-game-interview-v2.md` for the full design.
 */
/**
 * Type-guard narrowing a persisted status to the closed consumer
 * union. Consumers (the badge wire format, the kickoff switch) call
 * this before treating the value as `BuildGameInterviewStatus`.
 */
export function isKnownBuildGameInterviewStatus(status) {
    return (status === 'in-progress' ||
        status === 'complete' ||
        status === 'dispatched' ||
        status === 'building' ||
        status === 'built' ||
        status === 'built-partial' ||
        status === 'abandoned');
}
/**
 * Is this status one of the two TERMINAL build outcomes — the build ran to a
 * finish (`'built'`) or bailed out with partial deliverables
 * (`'built-partial'`)?
 *
 * Canonical home (crucible#3571). The renderer and the main-process
 * build-kickoff guard both need "is this game already built?", and the
 * predicate previously existed only as private copies (a local
 * `TERMINAL_STATUSES` set in the workspace run-state derivation, a local
 * `isTerminalInterviewStatus` in the Workspace page). Accepts the PERSISTED
 * (wide) status so on-disk reads don't need to narrow first; an unknown future
 * status is NOT treated as terminal — the conservative direction, since
 * mis-reading a live build as finished would suppress a legitimate resume.
 */
export function isTerminalBuildGameInterviewStatus(status) {
    return status === 'built' || status === 'built-partial';
}
/**
 * Type-guard narrowing a persisted interview depth to the closed
 * consumer union. Consumers (the renderer copy, the PhaseStrip
 * mode-aware copy) call this before treating the value as
 * `KnownInterviewDepth`.
 */
export function isKnownInterviewDepth(depth) {
    return depth === 'quick' || depth === 'full' || depth === 'advanced';
}
/**
 * Type-guard narrowing a persisted sub-phase to the closed consumer
 * union.
 */
export function isKnownInterviewSubPhase(subPhase) {
    return (subPhase === 'quick' ||
        subPhase === 'plan' ||
        subPhase === 'refine' ||
        subPhase === 'committed' ||
        subPhase === 'build' ||
        subPhase === 'back-to-plan');
}
/**
 * Return the effective interview depth for a given state, applying
 * the #191 backward-compat rule: absent → `'full'` (the legacy
 * conversational mode that every pre-#191 state file is implicitly
 * in). Returns the wide `KnownInterviewDepth` literal so callers
 * can switch exhaustively. An unknown future depth is also folded
 * to `'full'` on the renderer side — same defensive narrowing the
 * status summary uses for `BuildGameInterviewStatus`.
 *
 * Centralising this rule keeps the legacy default in one place;
 * spread-out `state.depth ?? 'full'` checks at call sites have a
 * habit of drifting (one site narrows, another doesn't). Hello-
 * weekend SKILL paired PR (feat/191-quick-batch-interview) is the
 * producer-side counterpart.
 */
export function getEffectiveInterviewDepth(state) {
    const depth = state.depth;
    if (depth === undefined)
        return 'full';
    return isKnownInterviewDepth(depth) ? depth : 'full';
}
/**
 * Return the effective sub-phase for a given state. Returns
 * `undefined` rather than a literal default because pre-#191
 * state files have no quick-batch sub-phase at all — the
 * interview is in the legacy `'full'` mode, and the sub-phase
 * concept simply doesn't apply. Callers that need to render
 * MUST first check `getEffectiveInterviewDepth(state) === 'quick'`;
 * only then is the sub-phase meaningful.
 *
 * Unknown future sub-phases are FOLDED to `undefined` by this
 * helper (defensive narrowing — the renderer can't exhaustively
 * switch on a value it doesn't know yet). The on-disk file still
 * preserves the verbatim string for forward compat — readers that
 * want the raw value can hit `state.subPhase` directly. This
 * matches the pattern from #534 / `interviewStatusSummary` where
 * the persisted state is wide and the consumer wire format is
 * narrowed.
 */
export function getEffectiveInterviewSubPhase(state) {
    const subPhase = state.subPhase;
    if (subPhase === undefined)
        return undefined;
    return isKnownInterviewSubPhase(subPhase) ? subPhase : undefined;
}
/**
 * Return the effective plan markdown for a given state. Absent →
 * empty string. Callers that need to render the markdown MUST
 * also gate on `getEffectiveInterviewDepth(state) === 'quick'`
 * (a `'full'` interview never has a plan).
 */
export function getEffectivePlan(state) {
    return state.plan ?? '';
}
//# sourceMappingURL=build-game-types.js.map